import * as React from "react";
import { Condition, ConditionOperator, Rule, TemplateContext } from "@emisgroup/clint-templates-common";
import { useTranslation } from "@emisgroup/application-intl";
import { HidePassword, ShowPassword } from "@emisgroup/icons-react";
import { RadioButton } from "@emisgroup/ui-radio-button";
import { IRadioItem } from "../types";
import "./visibilityRules.css";
import { getConditionsForDisplay } from "../utils/ruleUtils";
import VisibilityCondition from "./visibilityCondition";

type VisibilityRulesListProps = {
    canvasItemLabel: string;
    rule: Rule;
    invalidRuleConditions: any;
    canEditRules: boolean;
    onDelete: (conditionIndex: number) => void;
    onUpdate: (conditionIndex: number, updatedCondition: Condition) => void;
    onSelectNewSource: (conditionIndex: number) => void;
    onRuleAddOrUpdate?: (rule: Rule | null) => void;
};

const VisibilityRulesList = ({
    canvasItemLabel,
    rule,
    invalidRuleConditions = [],
    canEditRules,
    onDelete,
    onUpdate,
    onSelectNewSource,
    onRuleAddOrUpdate = () => ({}),
}: VisibilityRulesListProps) => {
    const { t } = useTranslation();
    const { templateDefinition } = React.useContext(TemplateContext);

    const componentVisibilityHeader = (rulesDefined: boolean) => {
        const visibilityTitle = t(
            rulesDefined ? "templates.rules.componentVisibleWhen" : "templates.rules.componentIsAlwaysVisible",
        );

        return (
            <>
                <div className="visibility-rules-list__header">
                    <div>
                        {rulesDefined ? (
                            <HidePassword title={visibilityTitle} />
                        ) : (
                            <ShowPassword title={visibilityTitle} />
                        )}
                    </div>
                    <div className="visibility-rules-list__title">{visibilityTitle}</div>
                </div>
            </>
        );
    };

    const conditionsDefined = rule ? rule.conditionMembers.length > 0 : false;
    const conditionsForDisplay = rule ? getConditionsForDisplay(t, rule, templateDefinition.members) : [];
    const conditionOperators: IRadioItem[] = [
        {
            text: t("templates.rules.allRules"),
            value: ConditionOperator.ALL,
            disabled: !canEditRules,
        },
        {
            text: t("templates.rules.anyRules"),
            value: ConditionOperator.ANY,
            disabled: !canEditRules,
        },
    ];

    const updateConditionOperator = (radioItem: ConditionOperator, index: number) => {
        const newConditionOperators = {
            ...(rule.conditionOperatorsCombination || {}),
            ...(index + 1 < conditionsForDisplay.length && { [index]: radioItem }), // Exclude the last condition
        };

        const uniqueOperators = [...new Set(Object.values(newConditionOperators))];
        const globalOperator = uniqueOperators.length === 1 ? uniqueOperators[0] : ConditionOperator.CUSTOM;

        const newRuleAfterUpdate: Rule = {
            ...rule,
            conditionOperatorsCombination: newConditionOperators,
            conditionOperator: globalOperator as ConditionOperator,
        };

        onRuleAddOrUpdate(newRuleAfterUpdate);
    };
    return (
        <div className="visibility-rules-list" id="visibility-rules-list">
            {componentVisibilityHeader(conditionsDefined)}
            {conditionsForDisplay.map((conditionForDisplay, index) => {
                const isAndOperator = rule.conditionOperatorsCombination?.[index] === ConditionOperator.ALL;
                const isPreviousAndOperator =
                    index > 0 && rule.conditionOperatorsCombination?.[index - 1] === ConditionOperator.ALL;

                return (
                    <React.Fragment key={conditionForDisplay.key}>
                        <div
                            className={`visibility-rules-list__condition ${
                                isAndOperator || isPreviousAndOperator
                                    ? "visibility-rules-list__condition-highlight"
                                    : ""
                            }`}
                        >
                            <VisibilityCondition
                                canvasItemLabel={canvasItemLabel}
                                conditionIndex={index}
                                conditionForDisplay={conditionForDisplay}
                                canEditRules={canEditRules}
                                invalidConditionFields={invalidRuleConditions[index.toString()]}
                                onDelete={() => onDelete(index)}
                                onUpdate={updatedCondition => onUpdate(index, updatedCondition)}
                                onSelectNewSource={() => onSelectNewSource(index)}
                                className="visibility-rules-condition"
                            />
                        </div>

                        {index + 1 < conditionsForDisplay.length && (
                            <div
                                data-testid="conditionOperator"
                                className={`sidebar-row-default visibility-rules-list__rule-box__join ${
                                    isAndOperator ? "visibility-rules-radiogroup-highlight" : ""
                                }`}
                                id="visibilityrules-customlist"
                            >
                                <RadioButton.Group
                                    ariaLabel="conditionOperator"
                                    name={`condition-operator-${index}`}
                                    value={rule.conditionOperatorsCombination?.[index]}
                                    onChange={selectedRadio =>
                                        updateConditionOperator(selectedRadio as ConditionOperator, index)
                                    }
                                    id={`visibility-rules-radio-group-${index}`}
                                    className="visibilityrules-customlist-radiogroup"
                                >
                                    {conditionOperators.map(option => {
                                        const optionValue = option.value as string;
                                        return (
                                            <RadioButton
                                                key={optionValue}
                                                value={optionValue}
                                                id={`${optionValue}-group-${index}`}
                                                className="visibility-rules-radio"
                                                aria-disabled={!canEditRules}
                                                disabled={!canEditRules}
                                            >
                                                {option.text}
                                            </RadioButton>
                                        );
                                    })}
                                </RadioButton.Group>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default VisibilityRulesList;
